import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';
import Icon from '../../components/base/Icon';

const QUOTE_FORM_URL = 'https://readdy.ai/api/form/d6nmvr2qoe30lj0v8rh0';

const SERVICE_OPTIONS = [
  { value: '', label: 'Select a service...' },
  { value: 'Fence Installation & Repair', label: 'Fence Installation & Repair' },
  { value: 'Handyman Services', label: 'Handyman Services' },
  { value: 'Bathroom Remodel', label: 'Bathroom Remodel' },
  { value: 'Small Remodel Project', label: 'Small Remodel Project' },
  { value: 'Other', label: 'Other' },
];

interface QuoteFormState {
  full_name: string;
  phone: string;
  email: string;
  service_needed: string;
  project_details: string;
}

const emptyForm = (): QuoteFormState => ({
  full_name: '',
  phone: '',
  email: '',
  service_needed: '',
  project_details: '',
});

function ServicesQuoteForm({ formId }: { formId: string }) {
  const [values, setValues] = useState<QuoteFormState>(emptyForm());
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = (): string => {
    if (!values.full_name.trim()) return 'Full name is required.';
    if (!values.phone.trim()) return 'Phone number is required.';
    if (!values.email.trim()) return 'Email address is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) return 'Please enter a valid email address.';
    if (!values.service_needed) return 'Please select a service.';
    if (!values.project_details.trim()) return 'Please describe your project.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setErrorMsg(validationError);
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const params = new URLSearchParams();
      (Object.keys(values) as (keyof QuoteFormState)[]).forEach((key) => {
        params.append(key, values[key]);
      });
      const res = await fetch(QUOTE_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (!res.ok) throw new Error('Submission failed');
      setStatus('success');
      setValues(emptyForm());
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or call us at (208) 204-6331.');
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm';

  return (
    <form onSubmit={handleSubmit} data-readdy-form id={formId} noValidate>
      {status === 'success' && (
        <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="check" className="w-4 h-4" /></span>
          <span>Thanks — we received your request and will contact you within 24 hours.</span>
        </div>
      )}
      {status === 'error' && errorMsg && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="circle-exclamation" className="w-4 h-4" /></span>
          <span>{errorMsg}</span>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor={`${formId}-full_name`} className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
          <input type="text" id={`${formId}-full_name`} name="full_name" required value={values.full_name} onChange={handleChange} className={inputClass} placeholder="John Smith" />
        </div>
        <div>
          <label htmlFor={`${formId}-phone`} className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
          <input type="tel" id={`${formId}-phone`} name="phone" required value={values.phone} onChange={handleChange} className={inputClass} placeholder="(208) 555-0123" />
        </div>
      </div>
      <div className="mb-4">
        <label htmlFor={`${formId}-email`} className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
        <input type="email" id={`${formId}-email`} name="email" required value={values.email} onChange={handleChange} className={inputClass} placeholder="john@example.com" />
      </div>
      <div className="mb-4">
        <label htmlFor={`${formId}-service_needed`} className="block text-sm font-medium text-gray-700 mb-1">Service Needed *</label>
        <select id={`${formId}-service_needed`} name="service_needed" required value={values.service_needed} onChange={handleChange} className={`${inputClass} cursor-pointer`}>
          {SERVICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="mb-6">
        <label htmlFor={`${formId}-project_details`} className="block text-sm font-medium text-gray-700 mb-1">Project Details *</label>
        <textarea id={`${formId}-project_details`} name="project_details" required rows={3} maxLength={500} value={values.project_details} onChange={handleChange} className={`${inputClass} resize-none`} placeholder="Describe your project — what needs to be done, approximate size, timeline..." />
        <p className="text-xs text-gray-500 mt-1">{values.project_details.length}/500 characters</p>
      </div>
      <button type="submit" disabled={status === 'submitting'} className="w-full bg-[#B45309] hover:bg-[#92400E] disabled:opacity-60 text-white px-6 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2">
        {status === 'submitting' ? 'Sending...' : (
          <>Request Free Quote <span className="w-5 h-5 flex items-center justify-center"><Icon name="arrow-right" className="w-4 h-4" /></span></>
        )}
      </button>
    </form>
  );
}

const services = [
  {
    id: 'fence',
    icon: 'border-all',
    title: 'Fence Installation & Repair',
    tagline: 'Wood, vinyl, chain link & custom gates',
    image: 'https://public.readdy.ai/ai/img_res/05877b4e995de7ffd77f7eca0dd1998a.jpg',
    description: 'From new privacy fences to storm-damaged post repairs, we handle every fence project across the Treasure Valley. We work with wood, vinyl, chain link, and composite materials — and we pull permits when required.',
    bullets: [
      'Wood privacy & picket fences',
      'Vinyl fence installation',
      'Chain link fencing',
      'Custom gate installation & repair',
      'Post replacement & fence repair',
      'HOA-compliant installs',
    ],
    cta: 'See Fence Services',
    link: '/services/fence',
    badge: 'Most Popular',
  },
  {
    id: 'handyman',
    icon: 'screwdriver-wrench',
    title: 'Handyman Services',
    tagline: 'Repairs, maintenance & small jobs done right',
    image: 'https://readdy.ai/api/search-image?query=skilled%20handyman%20repairing%20drywall%20in%20a%20bright%20modern%20home%20interior%20Boise%20Idaho%20professional%20tools%20clean%20workspace%20residential%20home%20improvement%20quality%20workmanship%20natural%20light&width=800&height=500&seq=svc-handyman-hub-01&orientation=landscape',
    description: 'No job too small. We handle the repairs and maintenance tasks that pile up — done right, on time, and without the runaround. Owner on every job.',
    bullets: [
      'Drywall repair & patching',
      'Interior & exterior painting',
      'Door & window adjustments',
      'Fixture & hardware installation',
      'Caulking & weatherstripping',
      'General home maintenance',
    ],
    cta: 'Handyman FAQ',
    link: '/services/handyman-faq',
    badge: null,
  },
  {
    id: 'bathroom',
    icon: 'bath',
    title: 'Bathroom Remodels',
    tagline: 'Full gut-and-rebuild or targeted upgrades',
    image: 'https://readdy.ai/api/search-image?query=beautifully%20remodeled%20modern%20bathroom%20with%20large%20format%20tile%20walk-in%20shower%20frameless%20glass%20door%20white%20vanity%20warm%20lighting%20Boise%20Idaho%20residential%20renovation%20clean%20contemporary%20design&width=800&height=500&seq=svc-bathroom-hub-01&orientation=landscape',
    description: 'Whether you want a full bathroom transformation or a targeted upgrade — new tile, vanity swap, or walk-in shower conversion — we deliver clean, lasting results.',
    bullets: [
      'Full bathroom gut & rebuild',
      'Tile installation (floor & walls)',
      'Vanity & fixture replacement',
      'Walk-in shower conversions',
      'Tub-to-shower conversions',
      'Lighting & exhaust fan upgrades',
    ],
    cta: 'Get a Remodel Quote',
    link: '#services-quote',
    badge: null,
  },
  {
    id: 'remodel',
    icon: 'hammer',
    title: 'Small Remodel Projects',
    tagline: 'Kitchen updates, basement finishing & fix-and-flip work',
    image: 'https://readdy.ai/api/search-image?query=modern%20kitchen%20remodel%20in%20progress%20with%20new%20cabinets%20countertops%20and%20flooring%20in%20a%20residential%20home%20Treasure%20Valley%20Idaho%20bright%20open%20layout%20warm%20wood%20tones%20professional%20renovation&width=800&height=500&seq=svc-remodel-hub-01&orientation=landscape',
    description: 'We specialize in the small-to-mid remodel projects that larger contractors pass on. From kitchen refreshes to basement finishing and fix-and-flip renovations, we keep projects on time and on budget.',
    bullets: [
      'Kitchen cabinet & countertop updates',
      'Basement finishing',
      'Laundry room upgrades',
      'Flooring installation',
      'Interior trim & millwork',
      'Fix-and-flip renovation support',
    ],
    cta: 'Get a Remodel Quote',
    link: '#services-quote',
    badge: 'Investor-Friendly',
  },
];

const serviceAreas = [
  'Boise', 'Meridian', 'Nampa', 'Caldwell', 'Eagle',
  'Star', 'Kuna', 'Middleton', 'Garden City', 'Beacon Light',
];

const faqs = [
  {
    q: 'What cities do you serve in the Treasure Valley?',
    a: 'We serve Boise, Meridian, Nampa, Caldwell, Eagle, Star, Kuna, Middleton, Garden City, and Beacon Light. These are our primary service areas, and we\'re on-site in these communities every week. We also take select projects in Emmett and Mountain Home — call us at (208) 204-6331 to confirm availability for your specific location before scheduling.',
  },
  {
    q: 'Are you licensed and insured in Idaho?',
    a: 'Yes — Fix Right Services is fully licensed and carries general liability insurance for all work performed in Idaho. We\'re happy to provide proof of insurance and our contractor registration before any work begins. This matters because hiring an unlicensed contractor can void your homeowner\'s insurance policy if something goes wrong on the job. We make it easy to verify our credentials upfront.',
  },
  {
    q: 'Are your estimates free, and how long does it take to get one?',
    a: 'Yes, estimates are completely free and come with no obligation. For most projects, we schedule a brief on-site visit first so we can see exactly what\'s involved — then we deliver a written, itemized estimate within 24–48 hours of that visit. We don\'t do verbal ballpark guesses. You\'ll have a clear, line-by-line document you can review at your own pace.',
  },
  {
    q: 'How quickly can you start my project?',
    a: 'Handyman and small repair jobs can often be scheduled within 3–7 business days. Fence installations and remodel projects typically start within 1–3 weeks, depending on current workload and material lead times. Spring and summer are our busiest seasons across the Treasure Valley — if you have a specific deadline or event date in mind, reach out early so we can lock in your spot on the schedule.',
  },
  {
    q: 'Do you pull permits for fence and remodeling work?',
    a: 'Yes. When a project requires a permit under Idaho state code or local Boise/Ada County/Canyon County building regulations, we handle the permit process on your behalf. We\'ll tell you during the estimate phase whether your project requires a permit, what the timeline looks like, and what it costs. You won\'t be surprised by permit requirements after work has already started.',
  },
  {
    q: 'Do you do roofing work?',
    a: 'No — roofing is outside our scope of work. We focus on fence installation and repair, handyman services, bathroom remodels, and small interior remodel projects. We stay in our lane so we can do those things exceptionally well. If you need a roofer, we\'re happy to refer you to a trusted local contractor we know personally.',
  },
  {
    q: 'Can you help with fix-and-flip renovation projects?',
    a: 'Absolutely. We work with real estate investors throughout the Treasure Valley on fix-and-flip projects regularly. We understand tight timelines, resale-focused budgets, and the need for a contractor who shows up and communicates clearly. We can coordinate multiple scopes under one contract — fencing, handyman repairs, bathroom remodels, flooring, and interior updates — which saves investors time and reduces the number of contractors they have to manage.',
  },
  {
    q: 'Who will actually be doing the work on my property?',
    a: 'The owner, Kole Southern, is personally involved in every project. You\'re not handing your home off to a subcontractor you\'ve never met or a crew that changes week to week. Kole is on-site, accountable, and reachable throughout the job. We believe that accountability starts at the top, and that\'s a standard we hold ourselves to on every single project.',
  },
  {
    q: 'What materials do you use for fence installations?',
    a: 'We work with wood (cedar and pressure-treated pine), vinyl, chain link, and composite fencing materials. The right choice depends on your budget, HOA requirements, desired maintenance level, and aesthetic goals. During the estimate visit, we\'ll walk you through the options that make sense for your property and give you pricing for each so you can make an informed decision.',
  },
  {
    q: 'How does your payment structure work?',
    a: 'We accept check, cash, and most major credit cards. For larger projects, payments are structured in milestones: a deposit to secure your start date and cover initial materials, one or more progress payments tied to defined project phases, and a final payment upon your approval at completion. We never ask for full payment upfront, and you won\'t be asked to pay the final balance until you\'re satisfied with the work.',
  },
  {
    q: 'What if I\'m not satisfied with the finished work?',
    a: 'We stand behind everything we do. If something isn\'t right — whether it\'s a finish detail, an alignment issue, or anything else — tell us and we\'ll make it right at no additional cost. We don\'t disappear after the job is done. Our reputation in the Treasure Valley is built entirely on doing the job correctly and treating customers fairly, and we take that seriously.',
  },
  {
    q: 'How do I get started?',
    a: 'The easiest way is to call or text us directly at (208) 204-6331 — we respond the same day. You can also fill out the free estimate form on this page and we\'ll follow up within 24 hours. Either way, the next step is a quick conversation about your project, followed by a free on-site visit and a written estimate. No pressure, no commitment required.',
  },
];

export default function ServicesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useSEO({
    title: 'Boise Fence, Handyman & Remodel Services | Fix Right',
    description: 'Licensed Boise contractor for fence installation, handyman repairs & bathroom remodels. Free written estimates. Serving Treasure Valley. Call (208) 204-6331.',
    keywords: 'fence installation Boise Idaho, handyman services Boise, bathroom remodel Boise, home remodeling Treasure Valley, licensed contractor Boise ID, fence repair Meridian Idaho, small remodel contractor Nampa, fix and flip contractor Boise, free estimate home services Boise, Treasure Valley fence contractor, insured handyman Boise Idaho',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/services`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': 'Boise Fence, Handyman & Remodel Services',
      'url': `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/services`,
      'description': 'Fix Right Services offers fence installation, handyman repairs, bathroom remodels, and small remodel projects across Boise and the Treasure Valley. Licensed, insured, free estimates.',
      'provider': {
        '@type': 'LocalBusiness',
        'name': 'Fix Right Services',
        'telephone': '+12082046331',
        'email': 'Southernkole@gmail.com',
        'url': import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': 'Boise',
          'addressRegion': 'ID',
          'addressCountry': 'US',
        },
      },
      'areaServed': serviceAreas,
      'hasOfferCatalog': {
        '@type': 'OfferCatalog',
        'name': 'Home Services — Treasure Valley',
        'itemListElement': [
          { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Fence Installation & Repair' } },
          { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Handyman Services' } },
          { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Bathroom Remodels' } },
          { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Small Remodel Projects' } },
        ],
      },
    },
  });

  return (
    <div className="min-h-screen bg-white">

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <Link to="/">
                <img
                  src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp"
                  alt="Fix Right Services Logo"
                  width="180"
                  height="56"
                  className="h-14 w-auto"
                  loading="eager"
                />
              </Link>
            </div>
            <div className="flex items-center gap-6">
              <div className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-700">
                <Link to="/" className="hover:text-[#B45309] transition-colors cursor-pointer">Home</Link>
                <Link to="/services" className="text-[#B45309] font-semibold cursor-pointer">Services</Link>
                <Link to="/about" className="hover:text-[#B45309] transition-colors cursor-pointer">About</Link>
                <Link to="/blog" className="hover:text-[#B45309] transition-colors cursor-pointer">Blog</Link>
              </div>
              <a
                href="tel:+12082046331"
                className="flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                aria-label="Call Fix Right Services"
              >
                <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
                <span className="hidden sm:inline">(208) 204-6331</span>
                <span className="sm:hidden">Call Now</span>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden" aria-label="Services hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20home%20contractor%20working%20on%20a%20beautiful%20residential%20property%20in%20Boise%20Idaho%20Treasure%20Valley%20neighborhood%20warm%20afternoon%20light%20tools%20craftsmanship%20quality%20home%20improvement%20suburban%20setting&width=1920&height=750&seq=services-hero-bg-01&orientation=landscape"
            alt="Fix Right Services — Boise Home Services"
            width="1920"
            height="750"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/65"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto w-full">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-white/70">
              <li><Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link></li>
              <li><span className="w-4 h-4 inline-flex items-center justify-center"><Icon name="chevron-right" className="w-3 h-3" /></span></li>
              <li className="text-white font-medium">Services</li>
            </ol>
          </nav>
          <div className="inline-flex items-center gap-2 bg-[#B45309]/90 text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
            <span className="w-4 h-4 flex items-center justify-center"><Icon name="location-dot" className="w-3.5 h-3.5" /></span>
            Boise &amp; Treasure Valley, Idaho
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Services in Boise &amp; Treasure Valley
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-3xl">
            Fix Right Services is your licensed, insured Boise contractor for fence installation, handyman repairs, bathroom remodels, and small remodel projects. Owner on every job. Free written estimates.
          </p>
          <div className="flex flex-wrap gap-4 mb-10">
            <a
              href="tel:+12082046331"
              className="inline-flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-8 py-3.5 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              Call (208) 204-6331
            </a>
            <a
              href="#services-quote"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-900 px-8 py-3.5 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="file-lines" className="w-4 h-4" /></span>
              Get Free Estimate
            </a>
          </div>
          <div className="flex flex-wrap gap-6 text-white/85 text-sm">
            {[
              { icon: 'shield-halved', text: 'Licensed & Insured in Idaho' },
              { icon: 'tag', text: 'Free written estimates' },
              { icon: 'clock', text: 'Response within 24 hours' },
              { icon: 'user-check', text: 'Owner on every job' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center"><Icon name={item.icon} className="w-4 h-4 text-[#F59E0B]" /></span>
                {item.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sticky Contact Bar */}
      <div className="sticky top-20 z-40 bg-gray-900 text-white py-3 px-4 sm:px-6 lg:px-8 shadow-md">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-medium text-white/90">
            <strong className="text-white">Fix Right Services</strong> — Licensed &amp; Insured Boise Contractor
          </p>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <a href="tel:+12082046331" className="flex items-center gap-2 text-[#F59E0B] hover:text-white font-semibold transition-colors whitespace-nowrap cursor-pointer">
              <span className="w-4 h-4 flex items-center justify-center"><Icon name="phone" className="w-3.5 h-3.5" /></span>
              (208) 204-6331
            </a>
            <span className="text-white/30 hidden sm:inline">|</span>
            <a href="mailto:Southernkole@gmail.com" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
              <span className="w-4 h-4 flex items-center justify-center"><Icon name="envelope" className="w-3.5 h-3.5" /></span>
              <span className="hidden sm:inline">Southernkole@gmail.com</span>
              <span className="sm:hidden">Email Us</span>
            </a>
            <a href="#services-quote" className="bg-[#B45309] hover:bg-[#92400E] text-white px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer">
              Free Quote
            </a>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Our services" id="services">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">What We Do</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Four core services. One reliable contractor. No roofing, no surprises.
            </p>
          </div>

          <div className="space-y-16">
            {services.map((svc, idx) => (
              <article
                key={svc.id}
                id={svc.id}
                className={`grid lg:grid-cols-2 gap-12 items-center ${idx % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}
              >
                <div className={`relative rounded-2xl overflow-hidden shadow-md h-80 ${idx % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <img
                    src={svc.image}
                    alt={`${svc.title} in Boise Idaho`}
                    width="800"
                    height="500"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  {svc.badge && (
                    <div className="absolute top-4 left-4 bg-[#B45309] text-white text-xs font-bold px-3 py-1.5 rounded-full">
                      {svc.badge}
                    </div>
                  )}
                </div>
                <div className={idx % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-12 h-12 flex items-center justify-center bg-[#B45309]/10 rounded-xl">
                      <Icon name={svc.icon} className="w-6 h-6 text-[#B45309]" />
                    </span>
                    <div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{svc.title}</h2>
                      <p className="text-[#B45309] text-sm font-medium">{svc.tagline}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed">{svc.description}</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
                    {svc.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="check" className="w-4 h-4 text-[#B45309]" />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to={svc.link}
                      className="inline-flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm"
                    >
                      {svc.cta}
                      <span className="w-4 h-4 flex items-center justify-center"><Icon name="arrow-right" className="w-3.5 h-3.5" /></span>
                    </Link>
                    <a
                      href="tel:+12082046331"
                      className="inline-flex items-center gap-2 border border-gray-300 hover:border-[#B45309] text-gray-700 hover:text-[#B45309] px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm"
                    >
                      <span className="w-4 h-4 flex items-center justify-center"><Icon name="phone" className="w-3.5 h-3.5" /></span>
                      Call to Discuss
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* What We Don't Do */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-amber-50 border-y border-amber-100" aria-label="Scope of work">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-5">
            <span className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0 mt-1">
              <Icon name="circle-info" className="w-6 h-6 text-amber-700" />
            </span>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">What We Don&apos;t Do — No Surprises</h2>
              <p className="text-gray-700 text-sm leading-relaxed mb-3">
                We stay in our lane so we can do our work exceptionally well. <strong>We do not do roofing.</strong> We also don&apos;t take on large general contracting projects, HVAC, plumbing, or electrical work as standalone services. If you need something outside our scope, we&apos;ll tell you upfront and refer you to someone we trust.
              </p>
              <p className="text-gray-600 text-sm">
                Not sure if your project fits? Call us at{' '}
                <a href="tel:+12082046331" className="text-[#B45309] font-semibold hover:underline cursor-pointer">(208) 204-6331</a>{' '}
                and we&apos;ll give you a straight answer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Why choose Fix Right Services">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Why Treasure Valley Homeowners Choose Us</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We&apos;re not the biggest contractor in Boise — we&apos;re the one who shows up, does the work right, and stands behind it.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'user-check', title: 'Owner on Every Job', desc: 'Kole Southern is personally on-site for every project. No subcontractors you\'ve never met.' },
              { icon: 'shield-halved', title: 'Licensed & Insured', desc: 'Fully licensed and insured in Idaho. Proof of insurance available before any work starts.' },
              { icon: 'file-contract', title: 'Written Estimates', desc: 'Every estimate is written, itemized, and delivered within 24–48 hours of the site visit. No verbal guesses.' },
              { icon: 'clock', title: '24-Hour Response', desc: 'We respond to every inquiry within 24 hours. No ghosting, no runaround.' },
              { icon: 'star', title: '5-Star Rated', desc: 'Consistently 5-star reviews from Boise, Caldwell, Beacon Light, and across the Treasure Valley.' },
              { icon: 'handshake', title: 'Fair, Transparent Pricing', desc: 'What\'s in the estimate is what you pay. No surprise add-ons at the end of the job.' },
              { icon: 'rotate-left', title: 'We Make It Right', desc: 'If something isn\'t right, we fix it. Our reputation depends on it.' },
              { icon: 'map-location-dot', title: 'Truly Local', desc: 'We live and work in the Treasure Valley. We know the neighborhoods, the HOAs, and the local codes.' },
            ].map((item) => (
              <div key={item.title} className="bg-gray-50 rounded-xl p-6 hover:shadow-md transition-shadow">
                <span className="w-12 h-12 flex items-center justify-center bg-[#B45309]/10 rounded-lg mb-4">
                  <Icon name={item.icon} className="w-6 h-6 text-[#B45309]" />
                </span>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Service areas">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Service Areas</h2>
            <p className="text-gray-600">We serve homeowners and investors across the Treasure Valley.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {serviceAreas.map((area) => (
              <div key={area} className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-5 py-2.5 text-sm font-medium text-gray-700 hover:border-[#B45309] hover:text-[#B45309] transition-colors">
                <span className="w-4 h-4 flex items-center justify-center">
                  <Icon name="location-dot" className="w-3.5 h-3.5 text-[#B45309]" />
                </span>
                {area}
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-6">
            Also serving Emmett, Mountain Home, and surrounding areas — call to confirm availability.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Frequently asked questions" id="faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600">Straight answers to the questions we hear most often.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-100 transition-colors cursor-pointer"
                  aria-expanded={openFaq === idx}
                >
                  <h4 className="font-semibold text-gray-900 text-sm pr-4">
                    <a id={`faq-services-${idx}`}>{faq.q}</a>
                  </h4>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-[#B45309]">
                    <Icon name={openFaq === idx ? 'chevron-up' : 'chevron-down'} className="w-4 h-4" />
                  </span>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                'url': 'https://fixrightservices.com/services',
                'mainEntity': faqs.map((faq) => ({
                  '@type': 'Question',
                  'name': faq.q,
                  'acceptedAnswer': {
                    '@type': 'Answer',
                    'text': faq.a,
                  },
                })),
              }),
            }}
          />
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" id="services-quote" aria-label="Request a free quote">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Get Your Free Estimate</h2>
            <p className="text-gray-600">
              Tell us about your project and we&apos;ll respond within 24 hours with a free, written, no-obligation estimate.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-5 text-sm">
              <a href="tel:+12082046331" className="flex items-center gap-2 text-[#B45309] font-semibold hover:underline cursor-pointer">
                <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
                (208) 204-6331
              </a>
              <span className="text-gray-300">|</span>
              <a href="mailto:Southernkole@gmail.com" className="flex items-center gap-2 text-gray-600 hover:text-[#B45309] transition-colors cursor-pointer">
                <span className="w-5 h-5 flex items-center justify-center"><Icon name="envelope" className="w-4 h-4" /></span>
                Southernkole@gmail.com
              </a>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
            <ServicesQuoteForm formId="services-page-quote-form" />
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Related resources">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Helpful Resources for Boise Homeowners</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'Fence Installation Cost in Boise, Idaho', slug: '/blog/fence-installation-cost-boise-idaho', desc: 'Real cost breakdowns for wood, vinyl, and chain link fences in the Treasure Valley.' },
              { title: 'Handyman Services Cost in Boise', slug: '/blog/handyman-services-cost-boise-idaho', desc: 'What to expect to pay for common handyman jobs in the Boise area.' },
              { title: 'Bathroom Renovation ROI in Treasure Valley', slug: '/blog/bathroom-renovation-roi-treasure-valley', desc: 'How much value a bathroom remodel adds to your Boise-area home.' },
              { title: 'How to Choose a Remodeling Contractor in Boise', slug: '/blog/how-to-choose-remodeling-contractor-boise', desc: 'What to look for — and what to avoid — when hiring a contractor in Idaho.' },
              { title: 'Fix & Flip Contractor in Boise, Idaho', slug: '/blog/fix-flip-contractor-boise-idaho', desc: 'Why investors across the Treasure Valley trust Fix Right Services for renovation work.' },
              { title: 'Boise Fence Rules & Permit Requirements', slug: '/resources/boise-fence-rules-permits', desc: 'Everything you need to know about permits, HOA rules, and local fence regulations.' },
            ].map((post) => (
              <Link
                key={post.slug}
                to={post.slug}
                className="bg-gray-50 rounded-xl p-5 border border-gray-200 hover:shadow-md hover:border-[#B45309]/30 transition-all cursor-pointer group"
              >
                <h3 className="font-semibold text-gray-900 text-sm mb-2 group-hover:text-[#B45309] transition-colors">{post.title}</h3>
                <p className="text-gray-600 text-xs leading-relaxed mb-3">{post.desc}</p>
                <span className="text-[#B45309] text-xs font-semibold flex items-center gap-1">
                  Read article
                  <span className="w-4 h-4 flex items-center justify-center"><Icon name="arrow-right" className="w-3 h-3" /></span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp" alt="Fix Right Services" width="180" height="48" className="h-12 w-auto mb-4" loading="lazy" />
              <p className="text-gray-400 text-sm">Boise&apos;s trusted experts for fence installation, handyman services, and home remodels. Licensed &amp; insured in Idaho.</p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Services</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/services/fence" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Fence Installation &amp; Repair</Link></li>
                <li><Link to="/services/handyman-faq" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Handyman Services</Link></li>
                <li><a href="#bathroom" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Bathroom Remodels</a></li>
                <li><a href="#remodel" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Small Remodel Projects</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">About Us</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Blog &amp; Tips</Link></li>
                <li><Link to="/resources/boise-fence-rules-permits" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Boise Fence Permits</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Contact</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="phone" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="tel:+12082046331" className="hover:text-white transition-colors cursor-pointer">(208) 204-6331</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="envelope" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="mailto:Southernkole@gmail.com" className="hover:text-white transition-colors cursor-pointer">Southernkole@gmail.com</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="location-dot" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <span>Boise, Meridian, Nampa, Caldwell &amp; Treasure Valley</span>
                </li>
              </ul>
              <div className="flex gap-3 mt-4">
                <a href="https://www.facebook.com/profile.php?id=61582658047880" aria-label="Facebook" rel="nofollow noopener noreferrer" target="_blank" className="w-10 h-10 bg-gray-800 hover:bg-[#B45309] rounded-lg flex items-center justify-center transition-colors cursor-pointer">
                  <Icon name="facebook" className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/fixright_services/" aria-label="Instagram" rel="nofollow noopener noreferrer" target="_blank" className="w-10 h-10 bg-gray-800 hover:bg-[#B45309] rounded-lg flex items-center justify-center transition-colors cursor-pointer">
                  <Icon name="instagram" className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
